import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Printer, 
  Check, 
  X, 
  AlertCircle, 
  Mail, 
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Image as ImageIcon,
  DollarSign,
  Calendar,
  Users,
  CheckCircle,
  Clock,
  Zap,
  Eye,
  Settings,
  Send,
  Download,
  Crop,
  Move,
  ArrowLeft,
  ChevronRight,
  Link2,
  Edit,
  Plus,
  Trash2,
  Search,
  CheckSquare,
  Square,
  UserCheck,
  Upload
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import RichTextEditor from '../ui/RichTextEditor';
import './PrintCampaignPage.css';

/**
 * PrintCampaignPage - Standalone admin page for managing print campaigns
 * URL: /curator/collections/:collectionId/prints
 * 
 * Features:
 * - Campaign configuration and status
 * - Artwork quality scanning via Cloudinary
 * - Artist opt-in tracking
 * - Email sending triggers
 * - Masonry layout with infinite scroll
 * - Crop preview modal
 */
const PrintCampaignPage = () => {
  const { collectionId } = useParams();
  const navigate = useNavigate();

  // Collection & Campaign state
  const [collection, setCollection] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Quality scanning state
  const [qualityResults, setQualityResults] = useState({});
  const [scanningProgress, setScanningProgress] = useState({ current: 0, total: 0, isScanning: false });
  const [qualitySummary, setQualitySummary] = useState(null);
  
  // Opt-in tracking state
  const [optinRecords, setOptinRecords] = useState([]);
  const [optinStats, setOptinStats] = useState({ total: 0, accepted: 0, pending: 0 });
  const [optinSearch, setOptinSearch] = useState('');
  const [optinFilter, setOptinFilter] = useState('all'); // 'all', 'accepted', 'pending'
  const [expandedArtists, setExpandedArtists] = useState(new Set());
  const [artistArtworkPrintStatus, setArtistArtworkPrintStatus] = useState({}); // { artistUuid: [printStatus records] }
  const [togglingOptIn, setTogglingOptIn] = useState(null); // vc_id being toggled
  
  // UI state
  const [activeSection, setActiveSection] = useState('overview');
  
  // Search and selection state for quality tab
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArtworks, setSelectedArtworks] = useState(new Set());
  const [enablingPrints, setEnablingPrints] = useState(false);
  
  // Admin high-res upload state
  const [uploadingHighResId, setUploadingHighResId] = useState(null);

  // Infinite scroll state
  const [displayCount, setDisplayCount] = useState(20);
  const [loadingMore, setLoadingMore] = useState(false);
  const loaderRef = useRef(null);

  // Crop preview modal state
  const [cropModal, setCropModal] = useState({
    isOpen: false,
    artwork: null,
    quality: null,
    selectedSize: null
  });
  const [cropPosition, setCropPosition] = useState({ x: 0.5, y: 0.5 });

  // Campaign edit modal state
  const [editModal, setEditModal] = useState({
    isOpen: false,
    isCreating: false
  });
  const [editForm, setEditForm] = useState({
    title: '',
    subtitle: '',
    starts_at: '',
    ends_at: '',
    signing_event_dates: [],  // Array of { start: ISO string, end: ISO string }
    optin_terms: '',
    active: true,
    pricing: {
      '11x14': { cost: 66.57, price: 130, artist_pct: 0.70 },
      '12x12': { cost: 55.00, price: 110, artist_pct: 0.70 },
      '16x20': { cost: 85.00, price: 170, artist_pct: 0.70 },
      '18x18': { cost: 95.00, price: 190, artist_pct: 0.70 }
    }
  });
  const [savingCampaign, setSavingCampaign] = useState(false);

  // Minimum dimensions for print sizes at 150 DPI (acceptable)
  const MIN_DIMENSIONS = {
    '11x14': { width: 1650, height: 2100, label: '11×14"' },
    '12x12': { width: 1800, height: 1800, label: '12×12"' },
    '16x20': { width: 2400, height: 3000, label: '16×20"' },
    '18x18': { width: 2700, height: 2700, label: '18×18"' },
  };

  // Excellent quality at 300 DPI
  const EXCELLENT_DIMENSIONS = {
    '11x14': { width: 3300, height: 4200 },
    '12x12': { width: 3600, height: 3600 },
    '16x20': { width: 4800, height: 6000 },
    '18x18': { width: 5400, height: 5400 },
  };

  // Build optimized Cloudinary URL
  const getOptimizedImageUrl = (url, width = 350) => {
    if (!url) return '/images/placeholder-artwork.jpg';
    
    if (url.includes('cloudinary.com') && url.includes('/image/upload/')) {
      return url.replace(
        '/image/upload/',
        `/image/upload/w_${width},c_limit,q_auto,f_auto/`
      );
    }
    
    return url;
  };

  // Calculate crop percentage for a print size
  const calculateCropInfo = (imageWidth, imageHeight, printSize) => {
    if (!imageWidth || !imageHeight || !MIN_DIMENSIONS[printSize]) {
      return { cropPercent: 0, aspectMatch: false, imageAspect: 0, printAspect: 0 };
    }

    const dims = MIN_DIMENSIONS[printSize];
    const imageAspect = imageWidth / imageHeight;
    const printAspect = dims.width / dims.height;
    
    const printAspectFlipped = dims.height / dims.width;
    const diffOriginal = Math.abs(imageAspect - printAspect);
    const diffFlipped = Math.abs(imageAspect - printAspectFlipped);
    const usedPrintAspect = diffOriginal <= diffFlipped ? printAspect : printAspectFlipped;
    
    let cropPercent = 0;
    if (imageAspect > usedPrintAspect) {
      const usedWidth = imageHeight * usedPrintAspect;
      cropPercent = Math.round(((imageWidth - usedWidth) / imageWidth) * 100);
    } else if (imageAspect < usedPrintAspect) {
      const usedHeight = imageWidth / usedPrintAspect;
      cropPercent = Math.round(((imageHeight - usedHeight) / imageHeight) * 100);
    }
    
    const aspectMatch = cropPercent < 5;
    
    return { cropPercent, aspectMatch, imageAspect, printAspect: usedPrintAspect };
  };

  // Open crop preview modal
  const openCropModal = (artwork, quality, size) => {
    setCropModal({ isOpen: true, artwork, quality, selectedSize: size });
    setCropPosition({ x: 0.5, y: 0.5 });
  };

  // Close crop modal
  const closeCropModal = () => {
    setCropModal({ isOpen: false, artwork: null, quality: null, selectedSize: null });
  };

  // Toggle artwork selection
  const toggleArtworkSelection = (vcId) => {
    setSelectedArtworks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(vcId)) {
        newSet.delete(vcId);
      } else {
        newSet.add(vcId);
      }
      return newSet;
    });
  };

  // Select all visible artworks that are print-ready
  const selectAllVisible = () => {
    const printableArtworks = displayedArtworks.filter(a => {
      const quality = qualityResults[a.vc_id];
      return quality && quality.qualityLevel !== 'low';
    });
    setSelectedArtworks(new Set(printableArtworks.map(a => a.vc_id)));
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedArtworks(new Set());
  };

  // Download opted-in artists and artworks as CSV
  const downloadOptInsCSV = () => {
    // Build data from accepted opt-in records with their accepted artworks
    const acceptedRecords = optinRecords.filter(r => r.optin_accepted);
    const rows = [];
    
    acceptedRecords.forEach(record => {
      const artistName = record.users?.nickname || 
        (record.users?.first_name && record.users?.last_name
          ? `${record.users.first_name} ${record.users.last_name}`
          : record.users?.first_name || record.users?.last_name || 'Unknown');
      const artistEmail = record.users?.email || '';
      
      // Get artworks that are BOTH accepted in the show AND belong to this artist
      const artistAcceptedArtworks = acceptedArtworks.filter(
        a => a.artist?.uuid === record.artist_uuid
      );
      
      if (artistAcceptedArtworks.length > 0) {
        artistAcceptedArtworks.forEach(artwork => {
          rows.push({
            artist: artistName,
            email: artistEmail,
            artwork: artwork.title || 'Unknown'
          });
        });
      } else {
        // Artist opted in but no accepted artworks
        rows.push({
          artist: artistName,
          email: artistEmail,
          artwork: '(No artworks in show)'
        });
      }
    });
    
    // Create CSV content
    const headers = ['Artist', 'Email', 'Artwork Title'];
    const csvContent = [
      headers.join(','),
      ...rows.map(row => 
        [row.artist, row.email, row.artwork]
          .map(field => `"${String(field).replace(/"/g, '""')}"`) // Escape quotes
          .join(',')
      )
    ].join('\n');
    
    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${campaign?.title || 'print-campaign'}-optins-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Download opted-in artists and artworks as Excel (XLSX)
  const downloadOptInsExcel = async () => {
    // Build data from accepted opt-in records with their accepted artworks
    const acceptedRecords = optinRecords.filter(r => r.optin_accepted);
    const rows = [];
    
    acceptedRecords.forEach(record => {
      const artistName = record.users?.nickname || 
        (record.users?.first_name && record.users?.last_name
          ? `${record.users.first_name} ${record.users.last_name}`
          : record.users?.first_name || record.users?.last_name || 'Unknown');
      const artistEmail = record.users?.email || '';
      
      // Get artworks that are BOTH accepted in the show AND belong to this artist
      const artistAcceptedArtworks = acceptedArtworks.filter(
        a => a.artist?.uuid === record.artist_uuid
      );
      
      if (artistAcceptedArtworks.length > 0) {
        artistAcceptedArtworks.forEach(artwork => {
          rows.push([artistName, artistEmail, artwork.title || 'Unknown']);
        });
      } else {
        rows.push([artistName, artistEmail, '(No artworks in show)']);
      }
    });
    
    // Create Excel-compatible XML (works without external libraries)
    const headers = ['Artist', 'Email', 'Artwork Title'];
    const escapeXml = (str) => String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    
    const xmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
  xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
  <Styles>
    <Style ss:ID="Header">
      <Font ss:Bold="1"/>
      <Interior ss:Color="#E2E8F0" ss:Pattern="Solid"/>
    </Style>
  </Styles>
  <Worksheet ss:Name="Opt-In Artworks">
    <Table>
      <Row>
        ${headers.map(h => `<Cell ss:StyleID="Header"><Data ss:Type="String">${escapeXml(h)}</Data></Cell>`).join('')}
      </Row>
      ${rows.map(row => `<Row>
        ${row.map(cell => `<Cell><Data ss:Type="String">${escapeXml(cell)}</Data></Cell>`).join('')}
      </Row>`).join('')}
    </Table>
  </Worksheet>
</Workbook>`;
    
    // Download file
    const blob = new Blob([xmlContent], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${campaign?.title || 'print-campaign'}-optins-${new Date().toISOString().split('T')[0]}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Admin opt-in: Enable print for selected artworks
  const enablePrintForSelected = async () => {
    if (selectedArtworks.size === 0 || !campaign) return;
    
    setEnablingPrints(true);
    
    try {
      // Get the selected artworks with their quality data
      const artworksToEnable = [...selectedArtworks].map(vcId => {
        const artwork = artworks.find(a => a.vc_id === vcId);
        const quality = qualityResults[vcId];
        return { vcId, artwork, quality };
      }).filter(item => item.quality && item.quality.qualityLevel !== 'low');

      // First ensure the artist has an opt-in record for this campaign
      const artistUuids = [...new Set(
        artworksToEnable
          .filter(item => item.artwork?.artist?.uuid)
          .map(item => item.artwork.artist.uuid)
      )];

      // Create/ensure opt-in records for these artists
      for (const artistUuid of artistUuids) {
        const { data: existingOptin } = await supabase
          .from('artist_print_optin')
          .select('id')
          .eq('artist_uuid', artistUuid)
          .eq('campaign_id', campaign.campaign_id)
          .maybeSingle();

        if (!existingOptin) {
          // Create opt-in record for artist, pre-accepted by admin
          await supabase
            .from('artist_print_optin')
            .insert({
              artist_uuid: artistUuid,
              campaign_id: campaign.campaign_id,
              optin_accepted: true,
              optin_at: new Date().toISOString(),
              terms_accepted: { admin_opted_in: true, admin_opted_in_at: new Date().toISOString() }
            });
        } else {
          // Ensure existing opt-in is marked as accepted
          await supabase
            .from('artist_print_optin')
            .update({
              optin_accepted: true,
              optin_at: new Date().toISOString(),
              terms_accepted: { admin_opted_in: true, admin_opted_in_at: new Date().toISOString() }
            })
            .eq('id', existingOptin.id);
        }
      }

      // Now enable print on each artwork_print_status record
      for (const { vcId, quality } of artworksToEnable) {
        const { data: existing } = await supabase
          .from('artwork_print_status')
          .select('id')
          .eq('vc_id', vcId)
          .is('optin_id', null)
          .maybeSingle();

        if (existing) {
          await supabase
            .from('artwork_print_status')
            .update({ print_enabled: true })
            .eq('id', existing.id);
        } else {
          await supabase
            .from('artwork_print_status')
            .insert({
              vc_id: vcId,
              optin_id: null,
              image_width: quality.width,
              image_height: quality.height,
              image_bytes: quality.bytes,
              quality_level: quality.qualityLevel,
              available_sizes: quality.availableSizes,
              quality_checked_at: new Date().toISOString(),
              print_enabled: true
            });
        }
      }

      // Update local state to reflect changes
      const updatedResults = { ...qualityResults };
      for (const { vcId } of artworksToEnable) {
        if (updatedResults[vcId]) {
          updatedResults[vcId] = { ...updatedResults[vcId], printEnabled: true };
        }
      }
      setQualityResults(updatedResults);

      // Clear selection and reload data
      setSelectedArtworks(new Set());
      await loadPageData();

      alert(`Successfully enabled print for ${artworksToEnable.length} artwork(s)`);
    } catch (err) {
      console.error('Error enabling prints:', err);
      alert('Error enabling prints: ' + err.message);
    } finally {
      setEnablingPrints(false);
    }
  };

  // Load all data on mount
  useEffect(() => {
    loadPageData();
  }, [collectionId]);

  // Get accepted artworks with search filtering
  const acceptedArtworks = artworks.filter(a => a.accepted);
  
  // Filter by search query (title or artist nickname)
  const filteredArtworks = searchQuery.trim() 
    ? acceptedArtworks.filter(a => {
        const query = searchQuery.toLowerCase();
        const title = (a.title || '').toLowerCase();
        const nickname = (a.artist?.nickname || '').toLowerCase();
        const fullName = `${a.artist?.first_name || ''} ${a.artist?.last_name || ''}`.toLowerCase();
        return title.includes(query) || nickname.includes(query) || fullName.includes(query);
      })
    : acceptedArtworks;
  
  const displayedArtworks = filteredArtworks.slice(0, displayCount);
  const hasMoreArtworks = displayCount < filteredArtworks.length;

  // Load more artworks for infinite scroll
  const loadMoreArtworks = useCallback(() => {
    if (loadingMore) return;
    
    const currentAccepted = artworks.filter(a => a.accepted);
    if (displayCount >= currentAccepted.length) return;
    
    setLoadingMore(true);
    setTimeout(() => {
      setDisplayCount(prev => Math.min(prev + 20, currentAccepted.length));
      setLoadingMore(false);
    }, 300);
  }, [loadingMore, displayCount, artworks]);

  // Infinite scroll observer
  useEffect(() => {
    if (activeSection !== 'quality') return;
    
    const currentRef = loaderRef.current;
    if (!currentRef) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore) {
          loadMoreArtworks();
        }
      },
      { threshold: 0.1, rootMargin: '200px' }
    );

    observer.observe(currentRef);

    return () => {
      observer.unobserve(currentRef);
    };
  }, [loadingMore, loadMoreArtworks, activeSection]);

  // Load all page data
  const loadPageData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load collection
      const { data: collectionData, error: collectionError } = await supabase
        .from('collections')
        .select('*')
        .eq('collection_id', collectionId)
        .single();

      if (collectionError) throw collectionError;
      setCollection(collectionData);

      // Load campaign for this collection
      const { data: campaignData, error: campaignError } = await supabase
        .from('print_campaigns')
        .select('*')
        .eq('collection_id', collectionId)
        .eq('active', true)
        .maybeSingle();

      if (campaignError) throw campaignError;
      
      if (!campaignData) {
        setLoading(false);
        return; // No campaign - will show "no campaign" state
      }

      setCampaign(campaignData);

      // Initialize edit form if campaign exists
      if (campaignData) {
        // Helper to convert ISO to local datetime-local format for inputs
        const toLocalInput = (isoString) => {
          if (!isoString) return '';
          try {
            const date = new Date(isoString);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}`;
          } catch {
            return '';
          }
        };

        // Normalize signing events - convert legacy strings to objects
        const normalizedSigningEvents = (campaignData.signing_event_dates || []).map(event => {
          if (typeof event === 'string') {
            return { start: event, end: '' };
          }
          return event;
        });

        setEditForm({
          title: campaignData.title || '',
          subtitle: campaignData.subtitle || '',
          starts_at: toLocalInput(campaignData.starts_at),
          ends_at: toLocalInput(campaignData.ends_at),
          signing_event_dates: normalizedSigningEvents,
          optin_terms: campaignData.optin_terms || '',
          active: campaignData.active ?? true,
          pricing: campaignData.pricing || {
            '11x14': { cost: 66.57, price: 130, artist_pct: 0.70 },
            '12x12': { cost: 55.00, price: 110, artist_pct: 0.70 },
            '16x20': { cost: 85.00, price: 170, artist_pct: 0.70 },
            '18x18': { cost: 95.00, price: 190, artist_pct: 0.70 }
          }
        });
      }

      // Load artworks via collection_vc_mapping (vcs doesn't have collection_id directly)
      const { data: mappingData, error: mappingError } = await supabase
        .from('collection_vc_mapping')
        .select('vc_id, accepted')
        .eq('collection_id', collectionId);

      if (mappingError) throw mappingError;

      const vcIds = (mappingData || []).map(m => m.vc_id);
      const acceptedVcIds = new Set(
        (mappingData || []).filter(m => m.accepted).map(m => m.vc_id)
      );

      if (vcIds.length === 0) {
        setArtworks([]);
        setArtists([]);
        setLoading(false);
        return;
      }

      // Load artwork details - vcs.uuid is the artist's uuid
      const { data: artworksData, error: artworksError } = await supabase
        .from('vcs')
        .select('vc_id, title, cloudinary_url, cover_image, width, height, uuid')
        .in('vc_id', vcIds);

      if (artworksError) throw artworksError;

      // Get unique artist UUIDs from vcs.uuid and fetch user data
      const artistUuids = [...new Set((artworksData || []).map(a => a.uuid).filter(Boolean))];
      let usersMap = {};
      
      if (artistUuids.length > 0) {
        const { data: usersData } = await supabase
          .from('users')
          .select('uuid, first_name, last_name, nickname, email')
          .in('uuid', artistUuids);
        
        usersMap = (usersData || []).reduce((acc, u) => {
          acc[u.uuid] = u;
          return acc;
        }, {});
      }

      const enrichedArtworks = (artworksData || []).map(a => ({
        ...a,
        artist: usersMap[a.uuid] || null,
        accepted: acceptedVcIds.has(a.vc_id)
      }));

      setArtworks(enrichedArtworks);

      // Extract unique artists
      const uniqueArtists = [...new Map(
        enrichedArtworks
          .filter(a => a.accepted && a.artist?.uuid)
          .map(a => [a.artist.uuid, a.artist])
      ).values()];
      setArtists(uniqueArtists);

      // Load opt-in records
      const { data: optinData, error: optinError } = await supabase
        .from('artist_print_optin')
        .select('*')
        .eq('campaign_id', campaignData.campaign_id);

      if (optinError) throw optinError;

      // Enrich opt-in records with artist info
      const enrichedOptins = (optinData || []).map(optin => {
        const artist = uniqueArtists.find(a => a.uuid === optin.artist_uuid);
        return {
          ...optin,
          users: artist ? {
            uuid: artist.uuid,
            first_name: artist.first_name,
            last_name: artist.last_name,
            nickname: artist.nickname,
            email: artist.email
          } : null
        };
      });

      setOptinRecords(enrichedOptins);
      
      const accepted = enrichedOptins.filter(r => r.optin_accepted).length;
      setOptinStats({
        total: enrichedOptins.length,
        accepted,
        pending: enrichedOptins.length - accepted
      });

      // Load artwork print status per artist (for showing opted-in artworks with their sizes)
      // This uses optin_id to link to artist_print_optin records
      const optinIds = (optinData || []).map(o => o.id).filter(Boolean);
      if (optinIds.length > 0) {
        const { data: artworkPrintData, error: artworkPrintError } = await supabase
          .from('artwork_print_status')
          .select('*')
          .in('optin_id', optinIds);

        if (!artworkPrintError && artworkPrintData) {
          // Group by optin_id then map to artist_uuid
          const optinIdToArtist = (optinData || []).reduce((acc, o) => {
            acc[o.id] = o.artist_uuid;
            return acc;
          }, {});

          const artistPrintMap = {};
          artworkPrintData.forEach(status => {
            const artistUuid = optinIdToArtist[status.optin_id];
            if (artistUuid) {
              if (!artistPrintMap[artistUuid]) {
                artistPrintMap[artistUuid] = [];
              }
              // Find artwork info
              const artwork = enrichedArtworks.find(a => a.vc_id === status.vc_id);
              artistPrintMap[artistUuid].push({
                ...status,
                artwork: artwork || null
              });
            }
          });
          setArtistArtworkPrintStatus(artistPrintMap);
        }
      }

      // Load existing quality results
      const acceptedVcIdsList = enrichedArtworks.filter(a => a.accepted).map(a => a.vc_id);
      if (acceptedVcIdsList.length > 0) {
        const { data: statusData, error: statusError } = await supabase
          .from('artwork_print_status')
          .select('*')
          .in('vc_id', acceptedVcIdsList)
          .is('optin_id', null);

        if (!statusError && statusData) {
          const resultsMap = {};
          statusData.forEach(status => {
            resultsMap[status.vc_id] = {
              id: status.id,
              width: status.image_width,
              height: status.image_height,
              bytes: status.image_bytes,
              qualityLevel: status.quality_level,
              availableSizes: status.available_sizes || [],
              checkedAt: status.quality_checked_at,
              printEnabled: status.print_enabled || false,
              highresImageUrl: status.highres_image_url || null
            };
          });
          setQualityResults(resultsMap);
          // Pass the enriched artworks directly since state hasn't updated yet
          const acceptedList = enrichedArtworks.filter(a => a.accepted);
          updateQualitySummary(resultsMap, acceptedList);
        }
      }

    } catch (err) {
      console.error('Error loading print campaign data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Update quality summary counts
  // artworksList param allows passing fresh data during initial load (before state updates)
  const updateQualitySummary = (results, artworksList = null) => {
    const summary = { excellent: 0, good: 0, acceptable: 0, low: 0, unscanned: 0 };
    
    // Use passed artworks list or fall back to state-derived acceptedArtworks
    const artworksToCheck = artworksList || acceptedArtworks;
    
    artworksToCheck.forEach(artwork => {
      const quality = results[artwork.vc_id];
      if (!quality) {
        summary.unscanned++;
      } else {
        summary[quality.qualityLevel] = (summary[quality.qualityLevel] || 0) + 1;
      }
    });
    
    setQualitySummary(summary);
  };

  // Scan single artwork quality via Cloudinary
  const scanArtworkQuality = async (artwork) => {
    const imageUrl = artwork.cloudinary_url || artwork.cover_image;
    if (!imageUrl) return null;

    try {
      // Use Cloudinary fl_getinfo to get image dimensions
      let infoUrl = imageUrl;
      if (imageUrl.includes('cloudinary.com') && imageUrl.includes('/image/upload/')) {
        infoUrl = imageUrl.replace('/image/upload/', '/image/upload/fl_getinfo/');
      }

      const response = await fetch(infoUrl);
      if (!response.ok) throw new Error('Failed to fetch image info');

      const data = await response.json();
      const { width, height, bytes } = data.input || data;

      // Determine quality level and available sizes
      const availableSizes = [];
      let qualityLevel = 'low';

      Object.entries(MIN_DIMENSIONS).forEach(([size, dims]) => {
        // Check both orientations
        const fitsLandscape = width >= dims.width && height >= dims.height;
        const fitsPortrait = width >= dims.height && height >= dims.width;
        
        if (fitsLandscape || fitsPortrait) {
          availableSizes.push(size);
        }
      });

      // Determine overall quality
      const maxDim = Math.max(width, height);
      if (maxDim >= 4000) {
        qualityLevel = 'excellent';
      } else if (maxDim >= 3000) {
        qualityLevel = 'good';
      } else if (maxDim >= 2000) {
        qualityLevel = 'acceptable';
      }

      const result = {
        width,
        height,
        bytes,
        qualityLevel,
        availableSizes,
        checkedAt: new Date().toISOString()
      };

      // Save to database
      await saveQualityResult(artwork.vc_id, result);

      return result;
    } catch (err) {
      console.error('Error scanning artwork:', artwork.vc_id, err);
      return null;
    }
  };

  // Save quality result to database
  const saveQualityResult = async (vcId, quality) => {
    try {
      // Check if record exists
      const { data: existing } = await supabase
        .from('artwork_print_status')
        .select('id')
        .eq('vc_id', vcId)
        .is('optin_id', null)
        .maybeSingle();

      const record = {
        vc_id: vcId,
        optin_id: null,
        image_width: quality.width,
        image_height: quality.height,
        image_bytes: quality.bytes,
        quality_level: quality.qualityLevel,
        available_sizes: quality.availableSizes,
        quality_checked_at: quality.checkedAt,
        print_enabled: quality.qualityLevel !== 'low'
      };

      if (existing) {
        await supabase
          .from('artwork_print_status')
          .update(record)
          .eq('id', existing.id);
      } else {
        await supabase
          .from('artwork_print_status')
          .insert(record);
      }
    } catch (err) {
      console.error('Error saving quality result:', err);
    }
  };

  // Admin high-res image upload handler - uploads to Supabase Storage bucket
  const handleAdminHighResUpload = async (vcId, file) => {
    if (!file) return;

    setUploadingHighResId(vcId);

    try {
      // Check file size (100MB limit)
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > 100) {
        throw new Error(`File too large (${fileSizeMB.toFixed(1)}MB). Maximum is 100MB.`);
      }

      // Get image dimensions before upload
      const imageDimensions = await getImageDimensions(file);
      
      // Generate unique filename matching Flutter pattern
      const timestamp = Date.now();
      const extension = file.name.split('.').pop().toLowerCase();
      const fileName = `highres_${vcId}_${timestamp}.${extension}`;

      // Upload to Supabase Storage bucket 'highres_prints'
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('highres_prints')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true,
          contentType: file.type
        });

      if (uploadError) {
        throw new Error(uploadError.message);
      }

      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('highres_prints')
        .getPublicUrl(fileName);

      console.log('Uploaded high-res image to Supabase Storage:', publicUrl);

      // Calculate quality level and available sizes
      const maxDim = Math.max(imageDimensions.width, imageDimensions.height);
      let qualityLevel = 'low';
      if (maxDim >= 4000) qualityLevel = 'excellent';
      else if (maxDim >= 3000) qualityLevel = 'good';
      else if (maxDim >= 2000) qualityLevel = 'acceptable';

      const availableSizes = [];
      Object.entries(MIN_DIMENSIONS).forEach(([size, dims]) => {
        const fitsLandscape = imageDimensions.width >= dims.width && imageDimensions.height >= dims.height;
        const fitsPortrait = imageDimensions.width >= dims.height && imageDimensions.height >= dims.width;
        if (fitsLandscape || fitsPortrait) {
          availableSizes.push(size);
        }
      });

      // Check if record exists
      const { data: existing } = await supabase
        .from('artwork_print_status')
        .select('id')
        .eq('vc_id', vcId)
        .is('optin_id', null)
        .maybeSingle();

      const record = {
        vc_id: vcId,
        optin_id: null,
        highres_image_url: publicUrl,
        image_width: imageDimensions.width,
        image_height: imageDimensions.height,
        image_bytes: file.size,
        quality_level: qualityLevel,
        available_sizes: availableSizes,
        quality_checked_at: new Date().toISOString(),
        print_enabled: qualityLevel !== 'low'
      };

      if (existing) {
        await supabase
          .from('artwork_print_status')
          .update(record)
          .eq('id', existing.id);
      } else {
        await supabase
          .from('artwork_print_status')
          .insert(record);
      }

      // Update local state
      setQualityResults(prev => ({
        ...prev,
        [vcId]: {
          ...prev[vcId],
          width: imageDimensions.width,
          height: imageDimensions.height,
          bytes: file.size,
          qualityLevel,
          availableSizes,
          checkedAt: new Date().toISOString(),
          printEnabled: qualityLevel !== 'low',
          highresImageUrl: publicUrl
        }
      }));

      alert(`High-res image uploaded to Supabase Storage!\n${imageDimensions.width} × ${imageDimensions.height}px (${qualityLevel})`);

    } catch (err) {
      console.error('Error uploading high-res image:', err);
      alert('Failed to upload image: ' + err.message);
    } finally {
      setUploadingHighResId(null);
    }
  };

  // Helper to get image dimensions from a File object
  const getImageDimensions = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
        URL.revokeObjectURL(img.src);
      };
      img.onerror = () => {
        reject(new Error('Failed to load image'));
        URL.revokeObjectURL(img.src);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  // Admin toggle opt-in for individual artwork from quality check screen
  const handleAdminOptInToggle = async (vcId, enabled) => {
    setTogglingOptIn(vcId);
    try {
      // Check if print status record exists
      const { data: existing } = await supabase
        .from('artwork_print_status')
        .select('id')
        .eq('vc_id', vcId)
        .is('optin_id', null)
        .maybeSingle();

      // Get current quality info if we have it
      const quality = qualityResults[vcId];
      
      if (existing) {
        await supabase
          .from('artwork_print_status')
          .update({ print_enabled: enabled })
          .eq('id', existing.id);
      } else if (quality) {
        // Create new record with current quality data
        await supabase
          .from('artwork_print_status')
          .insert({
            vc_id: vcId,
            optin_id: null,
            print_enabled: enabled,
            image_width: quality.width,
            image_height: quality.height,
            image_bytes: quality.bytes,
            quality_level: quality.qualityLevel,
            available_sizes: quality.availableSizes || [],
            quality_checked_at: quality.checkedAt || new Date().toISOString()
          });
      } else {
        alert('Please scan this artwork first before enabling print opt-in.');
        setTogglingOptIn(null);
        return;
      }

      // Update local state
      setQualityResults(prev => ({
        ...prev,
        [vcId]: {
          ...prev[vcId],
          printEnabled: enabled
        }
      }));

    } catch (err) {
      console.error('Error toggling opt-in:', err);
      alert('Failed to toggle opt-in: ' + err.message);
    } finally {
      setTogglingOptIn(null);
    }
  };

  // Scan all artworks
  const scanAllArtworks = async () => {
    const toScan = acceptedArtworks;
    setScanningProgress({ current: 0, total: toScan.length, isScanning: true });

    const newResults = { ...qualityResults };

    for (let i = 0; i < toScan.length; i++) {
      const artwork = toScan[i];
      setScanningProgress(prev => ({ ...prev, current: i + 1 }));

      const result = await scanArtworkQuality(artwork);
      if (result) {
        newResults[artwork.vc_id] = result;
        setQualityResults({ ...newResults });
      }

      // Small delay to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    setScanningProgress({ current: 0, total: 0, isScanning: false });
    updateQualitySummary(newResults);
  };

  // Create opt-in records for eligible artists
  const createOptinRecords = async () => {
    try {
      const artistUuids = [...new Set(
        artworks
          .filter(a => a.accepted && a.artist?.uuid)
          .map(a => a.artist.uuid)
      )];

      const records = artistUuids.map(uuid => ({
        artist_uuid: uuid,
        campaign_id: campaign.campaign_id
      }));

      const { error } = await supabase
        .from('artist_print_optin')
        .upsert(records, { 
          onConflict: 'artist_uuid,campaign_id',
          ignoreDuplicates: true 
        });

      if (error) throw error;

      await loadPageData();
    } catch (err) {
      console.error('Error creating opt-in records:', err);
      setError(err.message);
    }
  };

  // Get quality badge color
  const getQualityColor = (level) => {
    switch (level) {
      case 'excellent': return '#16a34a';
      case 'good': return '#2563eb';
      case 'acceptable': return '#ca8a04';
      case 'low': return '#dc2626';
      default: return '#6b7280';
    }
  };

  // Get quality icon
  const getQualityIcon = (level) => {
    switch (level) {
      case 'excellent': return <CheckCircle size={14} />;
      case 'good': return <Check size={14} />;
      case 'acceptable': return <Clock size={14} />;
      case 'low': return <X size={14} />;
      default: return <AlertCircle size={14} />;
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Get eligible artists (accepted artworks with good quality)
  const eligibleArtists = artists.filter(artist => {
    const artistArtworks = artworks.filter(a => 
      a.accepted && a.artist?.uuid === artist.uuid
    );
    return artistArtworks.some(artwork => {
      const quality = qualityResults[artwork.vc_id];
      return quality && quality.qualityLevel !== 'low';
    });
  });

  // Loading state
  if (loading) {
    return (
      <div className="print-campaign-page loading">
        <RefreshCw className="spin" size={32} />
        <span>Loading print campaign...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="print-campaign-page error">
        <AlertCircle size={32} />
        <h3>Error Loading Campaign</h3>
        <p>{error}</p>
        <button onClick={loadPageData} className="btn-primary">
          <RefreshCw size={16} />
          Retry
        </button>
      </div>
    );
  }

  // Open edit modal for creating new campaign
  const openCreateModal = () => {
    setEditForm({
      title: `${collection?.title || 'Collection'} Print Campaign`,
      subtitle: '',
      starts_at: '',
      ends_at: '',
      signing_event_dates: [],
      optin_terms: 'I grant VibrantCast the right to produce and sell prints of my selected artwork during this campaign. I understand that I will receive the artist percentage of net sales as specified in the pricing terms.',
      active: true,
      pricing: {
        '11x14': { cost: 66.57, price: 130, artist_pct: 0.70 },
        '12x12': { cost: 55.00, price: 110, artist_pct: 0.70 },
        '16x20': { cost: 85.00, price: 170, artist_pct: 0.70 },
        '18x18': { cost: 95.00, price: 190, artist_pct: 0.70 }
      }
    });
    setEditModal({ isOpen: true, isCreating: true });
  };

  // Open edit modal for existing campaign
  const openEditModal = () => {
    if (!campaign) return;
    setEditForm({
      title: campaign.title || '',
      subtitle: campaign.subtitle || '',
      starts_at: campaign.starts_at ? campaign.starts_at.slice(0, 16) : '',
      ends_at: campaign.ends_at ? campaign.ends_at.slice(0, 16) : '',
      signing_event_dates: campaign.signing_event_dates || [],
      optin_terms: campaign.optin_terms || '',
      active: campaign.active ?? true,
      pricing: campaign.pricing || {
        '11x14': { cost: 66.57, price: 130, artist_pct: 0.70 },
        '12x12': { cost: 55.00, price: 110, artist_pct: 0.70 },
        '16x20': { cost: 85.00, price: 170, artist_pct: 0.70 },
        '18x18': { cost: 95.00, price: 190, artist_pct: 0.70 }
      }
    });
    setEditModal({ isOpen: true, isCreating: false });
  };

  // Close edit modal
  const closeEditModal = () => {
    setEditModal({ isOpen: false, isCreating: false });
  };

  // Save campaign (create or update)
  const saveCampaign = async () => {
    try {
      setSavingCampaign(true);

      // Filter out empty signing events and normalize format
      const validSigningEvents = editForm.signing_event_dates
        .filter(event => {
          if (typeof event === 'string') return event !== '';
          return event.start || event.end;
        })
        .map(event => {
          // Ensure object format
          if (typeof event === 'string') {
            return { start: event, end: '' };
          }
          return event;
        });

      const campaignData = {
        title: editForm.title,
        subtitle: editForm.subtitle,
        starts_at: editForm.starts_at ? new Date(editForm.starts_at).toISOString() : null,
        ends_at: editForm.ends_at ? new Date(editForm.ends_at).toISOString() : null,
        signing_event_dates: validSigningEvents,
        optin_terms: editForm.optin_terms,
        active: editForm.active,
        pricing: editForm.pricing,
        collection_id: parseInt(collectionId)
      };

      console.log('Saving campaign data:', campaignData);

      if (editModal.isCreating) {
        // Generate unique campaign_id
        campaignData.campaign_id = `campaign_${collectionId}_${Date.now()}`;
        
        const { data, error } = await supabase
          .from('print_campaigns')
          .insert(campaignData)
          .select()
          .single();

        if (error) throw error;
        setCampaign(data);
      } else {
        // Update existing campaign using campaign_id
        const { data, error } = await supabase
          .from('print_campaigns')
          .update(campaignData)
          .eq('campaign_id', campaign.campaign_id)
          .select()
          .single();

        if (error) throw error;
        setCampaign(data);
      }

      closeEditModal();
    } catch (err) {
      console.error('Error saving campaign:', err);
      alert('Failed to save campaign: ' + err.message);
    } finally {
      setSavingCampaign(false);
    }
  };

  // Add signing event with start and end times
  const addSigningEvent = () => {
    setEditForm(prev => ({
      ...prev,
      signing_event_dates: [...prev.signing_event_dates, { start: '', end: '' }]
    }));
  };

  // Update signing event start or end time
  const updateSigningEvent = (index, field, value) => {
    setEditForm(prev => {
      const updated = [...prev.signing_event_dates];
      // Handle legacy string format - convert to object
      if (typeof updated[index] === 'string') {
        updated[index] = { start: updated[index], end: '' };
      }
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, signing_event_dates: updated };
    });
  };

  // Remove signing event
  const removeSigningEvent = (index) => {
    setEditForm(prev => ({
      ...prev,
      signing_event_dates: prev.signing_event_dates.filter((_, i) => i !== index)
    }));
  };

  // Update pricing field
  const updatePricing = (size, field, value) => {
    setEditForm(prev => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        [size]: {
          ...prev.pricing[size],
          [field]: parseFloat(value) || 0
        }
      }
    }));
  };

  // No campaign state
  if (!campaign) {
    return (
      <div className="print-campaign-page no-campaign">
        <Printer size={48} />
        <h3>No Print Campaign</h3>
        <p>There is no active print campaign for this collection.</p>
        <div className="no-campaign-actions">
          <button className="btn-primary" onClick={openCreateModal}>
            <Plus size={16} />
            Create Print Campaign
          </button>
          <Link to={`/curator/collections/${collectionId}`} className="btn-secondary">
            <ArrowLeft size={16} />
            Back to Collection
          </Link>
        </div>

        {/* Edit/Create Modal */}
        {editModal.isOpen && (
          <CampaignEditModal
            editForm={editForm}
            setEditForm={setEditForm}
            isCreating={editModal.isCreating}
            onClose={closeEditModal}
            onSave={saveCampaign}
            saving={savingCampaign}
            addSigningEvent={addSigningEvent}
            updateSigningEvent={updateSigningEvent}
            removeSigningEvent={removeSigningEvent}
            updatePricing={updatePricing}
            MIN_DIMENSIONS={MIN_DIMENSIONS}
          />
        )}
      </div>
    );
  }

  return (
    <div className="print-campaign-page">
      {/* Breadcrumb */}
      <div className="page-breadcrumb">
        <Link to="/curator/collections">Collections</Link>
        <ChevronRight size={14} />
        <Link to={`/curator/collections/${collectionId}`}>{collection?.title || 'Collection'}</Link>
        <ChevronRight size={14} />
        <span>Print Campaign</span>
      </div>

      {/* Campaign Header */}
      <div className="campaign-header">
        <div className="campaign-info">
          <div className="campaign-badge">
            <Printer size={20} />
            <span>Print Campaign</span>
          </div>
          <h1>{campaign.title}</h1>
          <p className="campaign-subtitle">{campaign.subtitle}</p>
        </div>
        <div className="campaign-actions">
          <button className="btn-icon" onClick={openEditModal} title="Edit Campaign">
            <Edit size={18} />
          </button>
          <span className={`status-badge ${campaign.active ? 'active' : 'inactive'}`}>
            {campaign.active ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>

      {/* Edit Modal */}
      {editModal.isOpen && (
        <CampaignEditModal
          editForm={editForm}
          setEditForm={setEditForm}
          isCreating={editModal.isCreating}
          onClose={closeEditModal}
          onSave={saveCampaign}
          saving={savingCampaign}
          addSigningEvent={addSigningEvent}
          updateSigningEvent={updateSigningEvent}
          removeSigningEvent={removeSigningEvent}
          updatePricing={updatePricing}
          MIN_DIMENSIONS={MIN_DIMENSIONS}
        />
      )}

      {/* Quick Stats */}
      <div className="campaign-stats">
        <div className="stat-card">
          <ImageIcon size={20} />
          <div className="stat-content">
            <span className="stat-value">{acceptedArtworks.length}</span>
            <span className="stat-label">Accepted Artworks</span>
          </div>
        </div>
        <div className="stat-card">
          <Users size={20} />
          <div className="stat-content">
            <span className="stat-value">{eligibleArtists.length}</span>
            <span className="stat-label">Eligible Artists</span>
          </div>
        </div>
        <div className="stat-card">
          <CheckCircle size={20} />
          <div className="stat-content">
            <span className="stat-value">{optinStats.accepted}</span>
            <span className="stat-label">Opted In</span>
          </div>
        </div>
        <div className="stat-card">
          <Calendar size={20} />
          <div className="stat-content">
            <span className="stat-value">{campaign.signing_event_dates?.length || 0}</span>
            <span className="stat-label">Signing Events</span>
          </div>
        </div>
      </div>

      {/* Section Navigation */}
      <div className="section-tabs">
        <button 
          className={`section-tab ${activeSection === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveSection('overview')}
        >
          <Eye size={16} />
          Overview
        </button>
        <button 
          className={`section-tab ${activeSection === 'quality' ? 'active' : ''}`}
          onClick={() => setActiveSection('quality')}
        >
          <Zap size={16} />
          Quality Check
        </button>
        <button 
          className={`section-tab ${activeSection === 'optins' ? 'active' : ''}`}
          onClick={() => setActiveSection('optins')}
        >
          <Mail size={16} />
          Opt-Ins ({optinStats.accepted}/{optinStats.total})
        </button>
        <button 
          className={`section-tab ${activeSection === 'pricing' ? 'active' : ''}`}
          onClick={() => setActiveSection('pricing')}
        >
          <DollarSign size={16} />
          Pricing
        </button>
      </div>

      {/* Section Content */}
      <div className="section-content">
        {/* Overview Section */}
        {activeSection === 'overview' && (
          <div className="overview-section">
            {/* Quality Summary */}
            <div className="quality-summary-card">
              <h3>Artwork Quality Summary</h3>
              {qualitySummary ? (
                <div className="quality-bars">
                  {['excellent', 'good', 'acceptable', 'low'].map(level => (
                    <div className="quality-row" key={level}>
                      <span className="quality-label" style={{ color: getQualityColor(level) }}>
                        {getQualityIcon(level)} {level.charAt(0).toUpperCase() + level.slice(1)}
                      </span>
                      <div className="quality-bar">
                        <div 
                          className={`quality-fill ${level}`} 
                          style={{ width: `${((qualitySummary[level] || 0) / acceptedArtworks.length) * 100}%` }}
                        />
                      </div>
                      <span className="quality-count">{qualitySummary[level] || 0}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-data">No artworks scanned yet</p>
              )}
              <button className="btn-secondary full-width" onClick={scanAllArtworks}>
                <Zap size={16} />
                Scan All Artworks
              </button>
            </div>

            {/* Opt-in Status */}
            <div className="optin-status-card">
              <h3>Artist Opt-In Status</h3>
              <div className="optin-progress">
                <div className="progress-ring">
                  <svg viewBox="0 0 36 36">
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#e5e7eb"
                      strokeWidth="3"
                    />
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#8b5cf6"
                      strokeWidth="3"
                      strokeDasharray={`${optinStats.total > 0 ? (optinStats.accepted / optinStats.total) * 100 : 0}, 100`}
                    />
                  </svg>
                  <div className="progress-text">
                    <span className="progress-value">
                      {optinStats.total > 0 ? Math.round((optinStats.accepted / optinStats.total) * 100) : 0}%
                    </span>
                  </div>
                </div>
                <div className="optin-details">
                  <p><strong>{optinStats.accepted}</strong> artists opted in</p>
                  <p><strong>{optinStats.pending}</strong> pending responses</p>
                  <p><strong>{eligibleArtists.length - optinStats.total}</strong> not yet invited</p>
                </div>
              </div>
              {optinStats.total < eligibleArtists.length && (
                <button className="btn-primary full-width" onClick={createOptinRecords}>
                  <Users size={16} />
                  Create Opt-In Records ({eligibleArtists.length - optinStats.total} artists)
                </button>
              )}
            </div>

            {/* Signing Events */}
            {campaign.signing_event_dates && campaign.signing_event_dates.length > 0 && (
              <div className="signing-events-card">
                <h3>Signing Events</h3>
                <div className="event-list">
                  {campaign.signing_event_dates.map((date, index) => (
                    <div key={index} className="event-item">
                      <Calendar size={16} />
                      <span>{new Date(date).toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        month: 'long', 
                        day: 'numeric',
                        year: 'numeric'
                      })}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Quality Check Section */}
        {activeSection === 'quality' && (
          <div className="quality-check-section">
            <div className="quality-header">
              <h3>Artwork Quality Analysis</h3>
              <div className="quality-header-actions">
                <span className="artwork-count">
                  Showing {displayedArtworks.length} of {filteredArtworks.length}
                  {searchQuery && ` (filtered from ${acceptedArtworks.length})`}
                </span>
                <button 
                  className="btn-primary"
                  onClick={scanAllArtworks}
                  disabled={scanningProgress.isScanning}
                >
                  {scanningProgress.isScanning ? (
                    <>
                      <RefreshCw className="spin" size={16} />
                      {scanningProgress.current}/{scanningProgress.total}
                    </>
                  ) : (
                    <>
                      <Zap size={16} />
                      Scan All
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Search and Bulk Actions Bar */}
            <div className="quality-toolbar">
              <div className="search-box">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search by title or artist..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setDisplayCount(20);
                  }}
                />
                {searchQuery && (
                  <button 
                    className="clear-search"
                    onClick={() => setSearchQuery('')}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              
              <div className="bulk-actions">
                {selectedArtworks.size > 0 ? (
                  <>
                    <span className="selection-count">
                      {selectedArtworks.size} selected
                    </span>
                    <button 
                      className="btn-secondary"
                      onClick={clearSelection}
                    >
                      Clear
                    </button>
                    <button 
                      className="btn-primary"
                      onClick={enablePrintForSelected}
                      disabled={enablingPrints}
                    >
                      {enablingPrints ? (
                        <>
                          <RefreshCw className="spin" size={16} />
                          Enabling...
                        </>
                      ) : (
                        <>
                          <UserCheck size={16} />
                          Enable Print ({selectedArtworks.size})
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <button 
                    className="btn-secondary"
                    onClick={selectAllVisible}
                    disabled={!displayedArtworks.some(a => {
                      const q = qualityResults[a.vc_id];
                      return q && q.qualityLevel !== 'low';
                    })}
                  >
                    <CheckSquare size={16} />
                    Select Print-Ready
                  </button>
                )}
              </div>
            </div>

            {scanningProgress.isScanning && (
              <div className="scanning-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ width: `${(scanningProgress.current / scanningProgress.total) * 100}%` }}
                  />
                </div>
                <span>Scanning artwork {scanningProgress.current} of {scanningProgress.total}...</span>
              </div>
            )}

            {/* Masonry Grid */}
            <div className="artwork-quality-masonry">
              {displayedArtworks.map(artwork => {
                const quality = qualityResults[artwork.vc_id];
                const imageUrl = artwork.cloudinary_url || artwork.cover_image;
                const isSelected = selectedArtworks.has(artwork.vc_id);
                const canSelect = quality && quality.qualityLevel !== 'low';
                
                return (
                  <div 
                    key={artwork.vc_id} 
                    className={`artwork-quality-card ${isSelected ? 'selected' : ''} ${quality?.printEnabled ? 'print-enabled' : ''}`}
                  >
                    {/* Selection checkbox */}
                    {canSelect && (
                      <button 
                        className={`select-checkbox ${isSelected ? 'checked' : ''}`}
                        onClick={() => toggleArtworkSelection(artwork.vc_id)}
                        title={isSelected ? 'Deselect' : 'Select for print opt-in'}
                      >
                        {isSelected ? <CheckSquare size={18} /> : <Square size={18} />}
                      </button>
                    )}
                    
                    {/* Print enabled badge */}
                    {quality?.printEnabled && (
                      <div className="print-enabled-badge" title="Print enabled by admin">
                        <Printer size={12} />
                      </div>
                    )}
                    
                    <div className="artwork-image-wrapper">
                      <img 
                        src={getOptimizedImageUrl(imageUrl, 350)}
                        alt={artwork.title}
                        onError={(e) => {
                          e.target.src = '/images/placeholder-artwork.jpg';
                        }}
                      />
                      {quality && (
                        <div 
                          className={`quality-badge ${quality.qualityLevel}`}
                          style={{ backgroundColor: getQualityColor(quality.qualityLevel) }}
                        >
                          {getQualityIcon(quality.qualityLevel)}
                          <span>{quality.qualityLevel}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="artwork-quality-info">
                      <h4 className="artwork-title">{artwork.title}</h4>
                      <p className="artist-name">
                        {artwork.artist?.nickname || (artwork.artist?.first_name && artwork.artist?.last_name 
                          ? `${artwork.artist.first_name} ${artwork.artist.last_name}`
                          : artwork.artist?.first_name || artwork.artist?.last_name || artwork.artist?.email || 'Unknown Artist')}
                      </p>
                      
                      {quality ? (
                        <div className="quality-details">
                          {(artwork.width || artwork.height) && (
                            <div className="detail-row art-size">
                              <span className="detail-label">Art:</span>
                              <span>{artwork.width}" × {artwork.height}"</span>
                            </div>
                          )}
                          
                          <div className="detail-row image-size">
                            <span>{quality.width?.toLocaleString()} × {quality.height?.toLocaleString()}px</span>
                            {quality.bytes && (
                              <span className="file-size">({formatFileSize(quality.bytes)})</span>
                            )}
                          </div>
                          
                          <div className="available-sizes">
                            {Object.entries(MIN_DIMENSIONS).map(([size, dims]) => {
                              const isAvailable = quality.availableSizes.includes(size);
                              const cropInfo = calculateCropInfo(quality.width, quality.height, size);
                              
                              return (
                                <span 
                                  key={size} 
                                  className={`size-tag ${isAvailable ? 'available' : 'unavailable'} ${isAvailable && cropInfo.cropPercent > 0 ? 'has-crop' : ''}`}
                                  onClick={() => isAvailable && openCropModal(artwork, quality, size)}
                                  title={
                                    !isAvailable 
                                      ? `${dims.label} - Image too small` 
                                      : cropInfo.cropPercent > 0 
                                        ? `${dims.label} - ${cropInfo.cropPercent}% will be cropped. Click to preview.`
                                        : `${dims.label} - Perfect fit!`
                                  }
                                  style={{ cursor: isAvailable ? 'pointer' : 'default' }}
                                >
                                  {isAvailable ? <Check size={10} /> : <X size={10} />}
                                  {dims.label}
                                  {isAvailable && cropInfo.cropPercent > 0 && (
                                    <span className="crop-indicator">
                                      <Crop size={8} />
                                      {cropInfo.cropPercent}%
                                    </span>
                                  )}
                                </span>
                              );
                            })}
                          </div>
                          
                          {/* Admin upload high-res button */}
                          <div className="admin-upload-section">
                            <input
                              type="file"
                              id={`admin-upload-${artwork.vc_id}`}
                              accept="image/jpeg,image/png,image/tiff,image/heic,image/heif,image/webp"
                              style={{ display: 'none' }}
                              onChange={(e) => handleAdminHighResUpload(artwork.vc_id, e.target.files?.[0])}
                              disabled={uploadingHighResId === artwork.vc_id}
                            />
                            <label 
                              htmlFor={`admin-upload-${artwork.vc_id}`}
                              className={`admin-upload-btn ${uploadingHighResId === artwork.vc_id ? 'uploading' : ''} ${quality.highresImageUrl ? 'has-highres' : ''}`}
                              title={quality.highresImageUrl ? 'High-res uploaded - click to replace' : 'Upload high-res image'}
                            >
                              {uploadingHighResId === artwork.vc_id ? (
                                <><RefreshCw className="spin" size={12} /> Uploading...</>
                              ) : quality.highresImageUrl ? (
                                <><CheckCircle size={12} /> High-res ✓</>
                              ) : (
                                <><Upload size={12} /> Upload High-Res</>
                              )}
                            </label>
                            {quality.highresImageUrl && (
                              <a 
                                href={quality.highresImageUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="view-highres-link"
                                title="View high-res image"
                              >
                                <ExternalLink size={12} />
                              </a>
                            )}
                          </div>
                          
                          {/* Admin opt-in toggle */}
                          <div className="admin-optin-section">
                            <button
                              className={`admin-optin-btn ${quality.printEnabled ? 'enabled' : 'disabled'} ${togglingOptIn === artwork.vc_id ? 'toggling' : ''}`}
                              onClick={() => handleAdminOptInToggle(artwork.vc_id, !quality.printEnabled)}
                              disabled={togglingOptIn === artwork.vc_id || quality.qualityLevel === 'low'}
                              title={
                                quality.qualityLevel === 'low' 
                                  ? 'Image quality too low for printing' 
                                  : quality.printEnabled 
                                    ? 'Click to disable print opt-in' 
                                    : 'Click to enable print opt-in'
                              }
                            >
                              {togglingOptIn === artwork.vc_id ? (
                                <><RefreshCw className="spin" size={12} /> Updating...</>
                              ) : quality.printEnabled ? (
                                <><Printer size={12} /> Print Enabled</>
                              ) : (
                                <><Printer size={12} /> Enable Print</>
                              )}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="not-scanned">
                          <Clock size={12} />
                          <span>Not scanned yet</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {hasMoreArtworks && (
              <div ref={loaderRef} className="load-more-trigger">
                {loadingMore ? (
                  <div className="loading-more">
                    <RefreshCw className="spin" size={20} />
                    <span>Loading more...</span>
                  </div>
                ) : (
                  <span>Scroll for more</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Opt-Ins Section */}
        {activeSection === 'optins' && (
          <div className="optins-section">
            <div className="optins-header">
              <h3>Artist Opt-In Management</h3>
              <div className="header-actions">
                {optinStats.accepted > 0 && (
                  <div className="download-buttons">
                    <button 
                      className="btn-secondary"
                      onClick={downloadOptInsCSV}
                      title="Download CSV for printing labels"
                    >
                      <Download size={16} />
                      CSV
                    </button>
                    <button 
                      className="btn-secondary"
                      onClick={downloadOptInsExcel}
                      title="Download Excel for printing labels"
                    >
                      <Download size={16} />
                      Excel
                    </button>
                  </div>
                )}
                {optinStats.total < eligibleArtists.length && (
                  <button className="btn-secondary" onClick={createOptinRecords}>
                    <Users size={16} />
                    Create Records
                  </button>
                )}
                <button 
                  className="btn-primary"
                  disabled={optinRecords.filter(r => !r.email_sent_at).length === 0}
                >
                  <Send size={16} />
                  Send Opt-In Emails ({optinRecords.filter(r => !r.email_sent_at).length})
                </button>
              </div>
            </div>

            {/* Search and Filter Controls */}
            <div className="optins-controls">
              <div className="search-box">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search by artist name or email..."
                  value={optinSearch}
                  onChange={(e) => setOptinSearch(e.target.value)}
                />
                {optinSearch && (
                  <button className="clear-search" onClick={() => setOptinSearch('')}>
                    <X size={14} />
                  </button>
                )}
              </div>
              <div className="filter-buttons">
                <button 
                  className={`filter-btn ${optinFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setOptinFilter('all')}
                >
                  All ({optinStats.total})
                </button>
                <button 
                  className={`filter-btn ${optinFilter === 'accepted' ? 'active' : ''}`}
                  onClick={() => setOptinFilter('accepted')}
                >
                  <CheckCircle size={14} />
                  Accepted ({optinStats.accepted})
                </button>
                <button 
                  className={`filter-btn ${optinFilter === 'pending' ? 'active' : ''}`}
                  onClick={() => setOptinFilter('pending')}
                >
                  <Clock size={14} />
                  Pending ({optinStats.pending})
                </button>
              </div>
            </div>

            <div className="optins-table">
              <div className="table-header">
                <span></span>
                <span>Artist</span>
                <span>Email</span>
                <span>Status</span>
                <span>Artworks</span>
                <span>Invited</span>
                <span>Opted In</span>
                <span>Actions</span>
              </div>
              {optinRecords
                .filter(record => {
                  // Apply status filter
                  if (optinFilter === 'accepted' && !record.optin_accepted) return false;
                  if (optinFilter === 'pending' && record.optin_accepted) return false;
                  
                  // Apply search filter
                  if (optinSearch) {
                    const searchLower = optinSearch.toLowerCase();
                    const artistName = record.users?.nickname || 
                      `${record.users?.first_name || ''} ${record.users?.last_name || ''}`.trim() || '';
                    const email = record.users?.email || '';
                    return artistName.toLowerCase().includes(searchLower) || 
                           email.toLowerCase().includes(searchLower);
                  }
                  return true;
                })
                .map(record => {
                  const artistUuid = record.artist_uuid;
                  const artistPrintRecords = artistArtworkPrintStatus[artistUuid] || [];
                  const isExpanded = expandedArtists.has(record.id);
                  const optedInArtworks = artistPrintRecords.filter(r => r.print_enabled);
                  
                  return (
                    <React.Fragment key={record.id}>
                      <div className={`table-row ${isExpanded ? 'expanded' : ''}`}>
                        <span className="expand-col">
                          {optedInArtworks.length > 0 && (
                            <button 
                              className="expand-btn"
                              onClick={() => {
                                setExpandedArtists(prev => {
                                  const next = new Set(prev);
                                  if (next.has(record.id)) {
                                    next.delete(record.id);
                                  } else {
                                    next.add(record.id);
                                  }
                                  return next;
                                });
                              }}
                              title={isExpanded ? 'Collapse' : 'Show artworks'}
                            >
                              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                          )}
                        </span>
                        <span className="artist-name">
                          {record.users?.nickname || (record.users?.first_name && record.users?.last_name
                            ? `${record.users.first_name} ${record.users.last_name}`
                            : record.users?.first_name || record.users?.last_name || 'Unknown')}
                        </span>
                        <span className="email">{record.users?.email || '—'}</span>
                        <span className={`status ${record.optin_accepted ? 'accepted' : 'pending'}`}>
                          {record.optin_accepted ? (
                            <><CheckCircle size={14} /> Accepted</>
                          ) : (
                            <><Clock size={14} /> Pending</>
                          )}
                        </span>
                        <span className="artwork-count">
                          {optedInArtworks.length > 0 ? (
                            <span className="count-badge">{optedInArtworks.length}</span>
                          ) : (
                            <span className="no-artworks">—</span>
                          )}
                        </span>
                        <span className="date">
                          {record.email_sent_at 
                            ? new Date(record.email_sent_at).toLocaleDateString() 
                            : '—'}
                        </span>
                        <span className="date">
                          {record.optin_at 
                            ? new Date(record.optin_at).toLocaleDateString() 
                            : '—'}
                        </span>
                        <span className="actions">
                          <button 
                            className="icon-btn" 
                            title="Copy Opt-in Link (Flutter App)"
                            onClick={() => {
                              const optinUrl = `https://vibrantcast.com/print-optin/${record.optin_uuid}`;
                              navigator.clipboard.writeText(optinUrl);
                              alert('Opt-in link copied to clipboard!\n\n' + optinUrl);
                            }}
                          >
                            <Link2 size={14} />
                          </button>
                          <button 
                            className="icon-btn" 
                            title="Open Opt-in Page (Flutter App)"
                            onClick={() => window.open(`https://vibrantcast.com/print-optin/${record.optin_uuid}`, '_blank')}
                          >
                            <ExternalLink size={14} />
                          </button>
                          <button className="icon-btn" title="Send Reminder">
                            <Mail size={14} />
                          </button>
                        </span>
                      </div>
                      
                      {/* Expanded artwork details */}
                      {isExpanded && optedInArtworks.length > 0 && (
                        <div className="artwork-expansion">
                          <div className="artwork-list">
                            {optedInArtworks.map(printStatus => (
                              <div key={printStatus.id} className="opted-artwork-row">
                                <div className="artwork-thumbnail">
                                  <img 
                                    src={getOptimizedImageUrl(
                                      printStatus.artwork?.cloudinary_url || printStatus.artwork?.cover_image, 
                                      80
                                    )}
                                    alt={printStatus.artwork?.title || 'Artwork'}
                                    onError={(e) => { e.target.src = '/images/placeholder-artwork.jpg'; }}
                                  />
                                </div>
                                <div className="artwork-info">
                                  <span className="artwork-title">
                                    {printStatus.artwork?.title || 'Unknown'}
                                  </span>
                                  <span className="artwork-dimensions">
                                    {printStatus.image_width?.toLocaleString()} × {printStatus.image_height?.toLocaleString()}px
                                  </span>
                                </div>
                                <div className="selected-sizes">
                                  {(printStatus.offered_sizes || printStatus.available_sizes || []).map(size => (
                                    <span key={size} className="size-badge">
                                      {MIN_DIMENSIONS[size]?.label || size}
                                    </span>
                                  ))}
                                  {(!printStatus.offered_sizes?.length && !printStatus.available_sizes?.length) && (
                                    <span className="no-sizes">No sizes selected</span>
                                  )}
                                </div>
                                <div className="quality-indicator">
                                  <span 
                                    className={`quality-dot ${printStatus.quality_level}`}
                                    title={`Quality: ${printStatus.quality_level}`}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
            </div>
          </div>
        )}

        {/* Pricing Section */}
        {activeSection === 'pricing' && (
          <div className="pricing-section">
            <div className="pricing-header">
              <h3>Print Pricing</h3>
              <p className="pricing-subtitle">Giclée archival prints • 2 inch white mat • Slim wood frame, no glass (no glare)</p>
            </div>

            <div className="pricing-grid">
              {campaign.pricing && Object.entries(campaign.pricing).map(([size, pricing]) => (
                <div key={size} className="pricing-card">
                  <div className="size-info">
                    <h4>{size}</h4>
                    <span className="framed-size">Framed: {pricing.framed_size}</span>
                  </div>
                  <div className="price-display">
                    <span className="sale-price">${pricing.price}</span>
                  </div>
                  <div className="earnings-split">
                    <div className="earning artist">
                      <span className="label">Artist (70%)</span>
                      <span className="amount">${pricing.artist_earnings?.toFixed(2)}</span>
                    </div>
                    <div className="earning vc">
                      <span className="label">VibrantCast (30%)</span>
                      <span className="amount">${pricing.vc_earnings?.toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="cost-info">
                    <span>Production cost: ${pricing.cost?.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Crop Preview Modal */}
      {cropModal.isOpen && cropModal.artwork && cropModal.quality && (
        <CropPreviewModal
          artwork={cropModal.artwork}
          quality={cropModal.quality}
          selectedSize={cropModal.selectedSize}
          minDimensions={MIN_DIMENSIONS}
          cropPosition={cropPosition}
          setCropPosition={setCropPosition}
          onClose={closeCropModal}
          getOptimizedImageUrl={getOptimizedImageUrl}
        />
      )}
    </div>
  );
};

/**
 * CropPreviewModal - Shows crop preview for a print size
 */
const CropPreviewModal = ({ 
  artwork, 
  quality, 
  selectedSize, 
  minDimensions,
  cropPosition,
  setCropPosition,
  onClose,
  getOptimizedImageUrl
}) => {
  const containerRef = useRef(null);
  const isDragging = useRef(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const printDims = minDimensions[selectedSize];
  if (!printDims) return null;

  const imageAspect = quality.width / quality.height;
  const printAspect = printDims.width / printDims.height;
  
  const printAspectFlipped = printDims.height / printDims.width;
  const useFlipped = Math.abs(imageAspect - printAspectFlipped) < Math.abs(imageAspect - printAspect);
  const usedPrintAspect = useFlipped ? printAspectFlipped : printAspect;

  let cropWidth, cropHeight;
  if (imageAspect > usedPrintAspect) {
    cropHeight = 100;
    cropWidth = (usedPrintAspect / imageAspect) * 100;
  } else {
    cropWidth = 100;
    cropHeight = (imageAspect / usedPrintAspect) * 100;
  }

  const maxOffsetX = 100 - cropWidth;
  const maxOffsetY = 100 - cropHeight;
  const cropLeft = cropPosition.x * maxOffsetX;
  const cropTop = cropPosition.y * maxOffsetY;

  const cropPercent = Math.round(100 - (cropWidth * cropHeight / 100));

  const handleDragStart = (e) => {
    e.preventDefault();
    isDragging.current = true;
  };

  const handleDragMove = (e) => {
    if (!isDragging.current || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;

    let x = (clientX - rect.left) / rect.width;
    let y = (clientY - rect.top) / rect.height;

    x = Math.max(0, Math.min(1, x));
    y = Math.max(0, Math.min(1, y));

    setCropPosition({ x, y });
  };

  const handleDragEnd = () => {
    isDragging.current = false;
  };

  const handleSave = async () => {
    console.log('Saving crop position:', { 
      vc_id: artwork.vc_id, 
      size: selectedSize, 
      position: cropPosition 
    });
    onClose();
  };

  const imageUrl = artwork.cloudinary_url || artwork.cover_image;

  return (
    <div className="crop-modal-overlay" onClick={onClose}>
      <div className="crop-modal" onClick={e => e.stopPropagation()}>
        <div className="crop-modal-header">
          <h3>Crop Preview: {printDims.label}</h3>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="crop-modal-body">
          <div className="crop-info-panel">
            <h4>{artwork.title}</h4>
            <p className="artist-name">
              {artwork.artist?.nickname || (artwork.artist?.first_name && artwork.artist?.last_name
                ? `${artwork.artist.first_name} ${artwork.artist.last_name}`
                : artwork.artist?.first_name || artwork.artist?.last_name || artwork.artist?.email || 'Unknown Artist')}
            </p>
            
            <div className="crop-stats">
              <div className="stat">
                <span className="label">Image Size:</span>
                <span className="value">{quality.width?.toLocaleString()} × {quality.height?.toLocaleString()}px</span>
              </div>
              <div className="stat">
                <span className="label">Image Aspect:</span>
                <span className="value">{imageAspect.toFixed(2)}:1</span>
              </div>
              <div className="stat">
                <span className="label">Print Size:</span>
                <span className="value">{printDims.label} ({useFlipped ? 'portrait' : 'landscape'})</span>
              </div>
              <div className="stat">
                <span className="label">Print Aspect:</span>
                <span className="value">{usedPrintAspect.toFixed(2)}:1</span>
              </div>
              <div className="stat highlight">
                <span className="label">Crop Amount:</span>
                <span className={`value ${cropPercent > 10 ? 'warning' : cropPercent > 0 ? 'caution' : 'good'}`}>
                  {cropPercent}%
                </span>
              </div>
            </div>

            <div className="crop-instructions">
              <Move size={14} />
              <span>Drag the image to adjust crop position</span>
            </div>
          </div>

          <div 
            className="crop-preview-container"
            ref={containerRef}
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
            onMouseMove={handleDragMove}
            onMouseUp={handleDragEnd}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
          >
            <div className="crop-image-wrapper">
              <img 
                src={getOptimizedImageUrl(imageUrl, 800)}
                alt={artwork.title}
                onLoad={() => setImageLoaded(true)}
                draggable={false}
              />
              
              <div className="crop-overlay">
                <div className="dim-area top" style={{ height: `${cropTop}%` }} />
                <div className="dim-area bottom" style={{ height: `${100 - cropTop - cropHeight}%` }} />
                <div 
                  className="dim-area left"
                  style={{ top: `${cropTop}%`, height: `${cropHeight}%`, width: `${cropLeft}%` }}
                />
                <div 
                  className="dim-area right"
                  style={{ top: `${cropTop}%`, height: `${cropHeight}%`, width: `${100 - cropLeft - cropWidth}%` }}
                />
                
                <div 
                  className="crop-box"
                  style={{
                    left: `${cropLeft}%`,
                    top: `${cropTop}%`,
                    width: `${cropWidth}%`,
                    height: `${cropHeight}%`
                  }}
                >
                  <div className="crop-handle tl" />
                  <div className="crop-handle tr" />
                  <div className="crop-handle bl" />
                  <div className="crop-handle br" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="crop-modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={handleSave}>
            <Check size={16} />
            Save Crop Position
          </button>
        </div>
      </div>
    </div>
  );
};

// Campaign Edit/Create Modal Component
const CampaignEditModal = ({
  editForm,
  setEditForm,
  isCreating,
  onClose,
  onSave,
  saving,
  addSigningEvent,
  updateSigningEvent,
  removeSigningEvent,
  updatePricing,
  MIN_DIMENSIONS
}) => {
  const [activeTab, setActiveTab] = useState('basic');

  // Get user's time zone abbreviation
  const timeZoneAbbr = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
    .formatToParts(new Date())
    .find(part => part.type === 'timeZoneName')?.value || '';

  // Convert ISO string to local datetime-local input format (YYYY-MM-DDTHH:MM)
  const isoToLocalInput = (isoString) => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return '';
    }
  };

  // Format datetime for display (shows in user's local time zone)
  const formatEventDateTime = (isoString) => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      return date.toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short'
      });
    } catch {
      return isoString;
    }
  };

  return (
    <div className="campaign-edit-modal-overlay" onClick={onClose}>
      <div className="campaign-edit-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isCreating ? 'Create Print Campaign' : 'Edit Print Campaign'}</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-tabs">
          <button 
            className={`tab ${activeTab === 'basic' ? 'active' : ''}`}
            onClick={() => setActiveTab('basic')}
          >
            Basic Info
          </button>
          <button 
            className={`tab ${activeTab === 'dates' ? 'active' : ''}`}
            onClick={() => setActiveTab('dates')}
          >
            Dates & Events
          </button>
          <button 
            className={`tab ${activeTab === 'pricing' ? 'active' : ''}`}
            onClick={() => setActiveTab('pricing')}
          >
            Pricing
          </button>
          <button 
            className={`tab ${activeTab === 'terms' ? 'active' : ''}`}
            onClick={() => setActiveTab('terms')}
          >
            Terms
          </button>
        </div>

        <div className="modal-body">
          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <div className="form-section">
              <div className="form-group">
                <label>Campaign Title *</label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={e => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Spring 2026 Print Collection"
                />
              </div>

              <div className="form-group">
                <label>Subtitle</label>
                <input
                  type="text"
                  value={editForm.subtitle}
                  onChange={e => setEditForm(prev => ({ ...prev, subtitle: e.target.value }))}
                  placeholder="e.g., Limited edition artist prints"
                />
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={editForm.active}
                    onChange={e => setEditForm(prev => ({ ...prev, active: e.target.checked }))}
                  />
                  <span>Campaign Active</span>
                </label>
                <p className="help-text">When active, artists can opt-in and prints can be sold.</p>
              </div>
            </div>
          )}

          {/* Dates & Events Tab */}
          {activeTab === 'dates' && (
            <div className="form-section">
              <div className="form-row">
                <div className="form-group">
                  <label>Campaign Start</label>
                  <input
                    type="datetime-local"
                    value={editForm.starts_at}
                    onChange={e => setEditForm(prev => ({ ...prev, starts_at: e.target.value }))}
                  />
                </div>

                <div className="form-group">
                  <label>Campaign End</label>
                  <input
                    type="datetime-local"
                    value={editForm.ends_at}
                    onChange={e => setEditForm(prev => ({ ...prev, ends_at: e.target.value }))}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>
                  <Calendar size={16} />
                  Signing Events
                </label>
                <p className="help-text">Add signing events with start and end times.</p>
                
                <div className="signing-events-list">
                  {editForm.signing_event_dates.map((event, index) => {
                    // Handle legacy string format
                    const eventObj = typeof event === 'string' ? { start: event, end: '' } : event;
                    return (
                      <div key={index} className="signing-event-row">
                        <div className="event-times">
                          <div className="time-field">
                            <label>Start</label>
                            <input
                              type="datetime-local"
                              value={isoToLocalInput(eventObj.start)}
                              onChange={e => updateSigningEvent(index, 'start', e.target.value ? new Date(e.target.value).toISOString() : '')}
                            />
                          </div>
                          <div className="time-field">
                            <label>End</label>
                            <input
                              type="datetime-local"
                              value={isoToLocalInput(eventObj.end)}
                              onChange={e => updateSigningEvent(index, 'end', e.target.value ? new Date(e.target.value).toISOString() : '')}
                            />
                          </div>
                        </div>
                        <div className="event-preview">
                          {eventObj.start && (
                            <span>{formatEventDateTime(eventObj.start)}</span>
                          )}
                          {eventObj.start && eventObj.end && <span className="to-separator">to</span>}
                          {eventObj.end && (
                            <span>{formatEventDateTime(eventObj.end)}</span>
                          )}
                        </div>
                        <button 
                          type="button" 
                          className="btn-icon-danger"
                          onClick={() => removeSigningEvent(index)}
                          title="Remove event"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    );
                  })}
                  
                  <button type="button" className="btn-add-event" onClick={addSigningEvent}>
                    <Plus size={16} />
                    Add Signing Event
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Pricing Tab */}
          {activeTab === 'pricing' && (
            <div className="form-section">
              <p className="section-intro">Configure pricing for each print size. Artist percentage is the portion of profit (price minus cost) that goes to the artist.</p>
              
              <div className="pricing-grid">
                {Object.entries(MIN_DIMENSIONS).map(([size, dims]) => (
                  <div key={size} className="pricing-card">
                    <h4>{dims.label}</h4>
                    
                    <div className="pricing-fields">
                      <div className="field">
                        <label>Cost ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={editForm.pricing[size]?.cost || 0}
                          onChange={e => updatePricing(size, 'cost', e.target.value)}
                        />
                      </div>
                      
                      <div className="field">
                        <label>Price ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={editForm.pricing[size]?.price || 0}
                          onChange={e => updatePricing(size, 'price', e.target.value)}
                        />
                      </div>
                      
                      <div className="field">
                        <label>Artist %</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="1"
                          value={editForm.pricing[size]?.artist_pct || 0}
                          onChange={e => updatePricing(size, 'artist_pct', e.target.value)}
                        />
                      </div>
                    </div>
                    
                    <div className="pricing-summary">
                      <span className="profit">
                        Profit: ${((editForm.pricing[size]?.price || 0) - (editForm.pricing[size]?.cost || 0)).toFixed(2)}
                      </span>
                      <span className="artist-share">
                        Artist gets: ${(((editForm.pricing[size]?.price || 0) - (editForm.pricing[size]?.cost || 0)) * (editForm.pricing[size]?.artist_pct || 0)).toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Terms Tab */}
          {activeTab === 'terms' && (
            <div className="form-section">
              <div className="form-group">
                <label>Opt-In Terms</label>
                <p className="help-text">These terms will be shown to artists when they opt-in to the campaign. Use clear, concise language.</p>
                <div className="terms-editor-wrapper">
                  <RichTextEditor
                    value={editForm.optin_terms}
                    onChange={(value) => setEditForm(prev => ({ ...prev, optin_terms: value }))}
                    placeholder="Enter the terms artists must agree to..."
                    minHeight="250px"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn-primary" onClick={onSave} disabled={saving || !editForm.title}>
            {saving ? (
              <>
                <RefreshCw size={16} className="spin" />
                Saving...
              </>
            ) : (
              <>
                <Check size={16} />
                {isCreating ? 'Create Campaign' : 'Save Changes'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrintCampaignPage;
